import {
  loadGooglePlacesLibrary,
  readGoogleMapsAPIKey,
  type GoogleAddressComponent,
  type GooglePlaceDetails,
  type GooglePlacePrediction,
  type GooglePlacesLibrary,
} from "../../../lib/google-places";

export type PropertyAddressSelection = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  formattedAddress: string;
  latitude: number | null;
  longitude: number | null;
  postalCode: string;
  state: string;
};

export type PropertyAddressSuggestion = {
  id: string;
  label: string;
  mainText: string;
  prediction: GooglePlacePrediction;
  secondaryText: string | null;
};

export interface PropertyAddressProvider {
  search(query: string): Promise<PropertyAddressSuggestion[]>;
  select(
    suggestion: PropertyAddressSuggestion,
  ): Promise<PropertyAddressSelection>;
}

const PROPERTY_REGION_CODES = ["us"];
const PROPERTY_LOOKUP_RETRY_DELAY_MS = 150;
const providers = new Map<string, PropertyAddressProvider>();

type ProviderErrorCode = "configuration" | "load" | "quota" | "unavailable";

export class PropertyAddressProviderError extends Error {
  code: ProviderErrorCode;

  constructor(code: ProviderErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "PropertyAddressProviderError";
  }
}

export function getPropertyAddressProvider(): PropertyAddressProvider | null {
  const apiKey = readGoogleMapsAPIKey();
  if (!apiKey) {
    return null;
  }
  const cached = providers.get(apiKey);
  if (cached) {
    return cached;
  }
  const provider = new GooglePlacesPropertyAddressProvider(apiKey);
  providers.set(apiKey, provider);
  return provider;
}

class GooglePlacesPropertyAddressProvider implements PropertyAddressProvider {
  private readonly apiKey: string;
  private sessionToken: unknown | null = null;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  async search(query: string) {
    const trimmed = query.trim();
    if (!trimmed) {
      return [];
    }

    try {
      return await this.searchOnce(trimmed);
    } catch (error) {
      const providerError = toPropertyAddressProviderError(error);
      if (providerError.code !== "load") {
        throw providerError;
      }

      await sleep(PROPERTY_LOOKUP_RETRY_DELAY_MS);

      try {
        return await this.searchOnce(trimmed);
      } catch (retryError) {
        throw toPropertyAddressProviderError(retryError);
      }
    }
  }

  async select(suggestion: PropertyAddressSuggestion) {
    try {
      const place = suggestion.prediction.toPlace();
      const response = await place.fetchFields({
        fields: ["addressComponents", "formattedAddress", "location"],
      });
      this.sessionToken = null;
      return normalizeGooglePlaceSelection(response.place);
    } catch (error) {
      throw toPropertyAddressProviderError(error);
    }
  }

  private ensureSessionToken(library: GooglePlacesLibrary) {
    if (!this.sessionToken) {
      this.sessionToken = new library.AutocompleteSessionToken();
    }
  }

  private async searchOnce(trimmed: string) {
    const library = await loadGooglePlacesLibrary(this.apiKey);
    this.ensureSessionToken(library);
    const lookupInput = buildAutocompleteLookupInput(trimmed);
    const response =
      await library.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        includedRegionCodes: PROPERTY_REGION_CODES,
        input: lookupInput,
        sessionToken: this.sessionToken ?? undefined,
      });

    return response.suggestions.flatMap((suggestion) => {
      const prediction = suggestion.placePrediction;
      if (!prediction) {
        return [];
      }
      return {
        id: prediction.placeId,
        label: prediction.text.toString(),
        mainText: prediction.mainText?.toString() ?? prediction.text.toString(),
        prediction,
        secondaryText: prediction.secondaryText?.toString() ?? null,
      };
    });
  }
}

function buildAutocompleteLookupInput(query: string) {
  const normalized = query.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return "";
  }

  const primaryLine = normalized.split(/[,\n]/, 1)[0]?.trim() ?? normalized;
  return primaryLine || normalized;
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function normalizeGooglePlaceSelection(
  place: GooglePlaceDetails,
): PropertyAddressSelection {
  const addressComponents = place.addressComponents ?? [];
  const streetNumber = findAddressComponent(addressComponents, "street_number");
  const route = findAddressComponent(addressComponents, "route");
  const line2 = [
    findAddressComponent(addressComponents, "subpremise"),
    findAddressComponent(addressComponents, "floor"),
    findAddressComponent(addressComponents, "room"),
  ]
    .filter(Boolean)
    .join(", ");
  const city =
    findAddressComponent(addressComponents, "locality") ||
    findAddressComponent(addressComponents, "postal_town") ||
    findAddressComponent(addressComponents, "sublocality_level_1");
  const state =
    findAddressComponent(addressComponents, "administrative_area_level_1", {
      short: true,
    }) ||
    findAddressComponent(addressComponents, "administrative_area_level_1");
  const postalCode = buildPostalCode(addressComponents);
  const country =
    findAddressComponent(addressComponents, "country", {
      short: true,
    }) ||
    findAddressComponent(addressComponents, "country") ||
    "US";
  const addressLine1 = [streetNumber, route].filter(Boolean).join(" ").trim();
  const formattedAddress =
    place.formattedAddress?.trim() ||
    [addressLine1, city, state, postalCode].filter(Boolean).join(", ");

  if (!addressLine1 || !formattedAddress) {
    throw new PropertyAddressProviderError(
      "unavailable",
      "Google returned an incomplete address.",
    );
  }

  return {
    addressLine1,
    addressLine2: line2,
    city,
    country,
    formattedAddress,
    latitude: place.location?.lat() ?? null,
    longitude: place.location?.lng() ?? null,
    postalCode,
    state,
  };
}

function buildPostalCode(addressComponents: GoogleAddressComponent[]) {
  const postalCode = findAddressComponent(addressComponents, "postal_code");
  const postalSuffix = findAddressComponent(
    addressComponents,
    "postal_code_suffix",
  );
  return [postalCode, postalSuffix].filter(Boolean).join("-");
}

function findAddressComponent(
  addressComponents: GoogleAddressComponent[],
  type: string,
  options: { short?: boolean } = {},
) {
  const match = addressComponents.find((component) =>
    component.types.includes(type),
  );
  if (!match) {
    return "";
  }
  const value = options.short ? match.shortText : match.longText;
  return value?.trim() ?? "";
}

function toPropertyAddressProviderError(error: unknown) {
  if (error instanceof PropertyAddressProviderError) {
    return error;
  }
  const providerCode =
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof error.code === "number"
      ? error.code
      : null;
  const message = error instanceof Error ? error.message : "Unknown error";
  const normalized = message.toLowerCase();
  if (
    providerCode === 7 ||
    normalized.includes("permission_denied") ||
    normalized.includes("request_denied") ||
    normalized.includes("not been used in project") ||
    normalized.includes("disabled") ||
    normalized.includes("referer")
  ) {
    return new PropertyAddressProviderError(
      "configuration",
      "Google Places API access is not enabled for this browser key.",
    );
  }
  if (
    normalized.includes("quota") ||
    normalized.includes("billing") ||
    normalized.includes("over_query_limit")
  ) {
    return new PropertyAddressProviderError(
      "quota",
      "Google Places is temporarily unavailable.",
    );
  }
  if (
    normalized.includes("load") ||
    normalized.includes("network") ||
    normalized.includes("maps")
  ) {
    return new PropertyAddressProviderError(
      "load",
      "Google Places failed to load.",
    );
  }
  return new PropertyAddressProviderError(
    "unavailable",
    "Google Places is unavailable right now.",
  );
}
